/* ======================================================
   SACRED GEMS – CODEX INIT v5.0 (compact, keeps Level)
   - Bottom nav alignment
   - Smooth scroll + scrollspy
   - 12D Carousel builder:
       • copies dim-*d classes to cards (for background)
       • reads "Resonance Level" -> adds level-* class to card (for border)
       • COMPACT MODE: adds .cc-compact class and places Level first
   ====================================================== */
(function () {
  document.documentElement.classList.add('js-enabled');

  /* ---------- Bottom nav aligns to content column ---------- */
  const nav = document.getElementById('codex-tabs') || document.querySelector('.codex-tabs');
  const container = document.querySelector('.codex-container') || document.querySelector('.page-width');

  function alignNav() {
    if (!nav || !container) return;
    const r = container.getBoundingClientRect();
    nav.style.left   = Math.round(r.left + window.scrollX) + 'px';
    nav.style.width  = Math.round(r.width) + 'px';
    nav.style.right  = 'auto';
    nav.style.transform = 'none';
    nav.classList.add('is-aligned');
  }
  window.addEventListener('load', alignNav);
  window.addEventListener('resize', alignNav);
  window.addEventListener('orientationchange', alignNav);
  document.addEventListener('scroll', () => requestAnimationFrame(alignNav), { passive:true });

  /* ---------- Smooth scroll + scrollspy ---------- */
  const headerSel = 'header, .site-header, #shopify-section-header';
  const getHeaderH = () => {
    const h = document.querySelector(headerSel);
    return h ? Math.round(h.getBoundingClientRect().height) : 84;
  };

  const tabs = nav ? Array.from(nav.querySelectorAll('.ctab')) : [];
  const ids  = tabs.map(a => a.getAttribute('href')?.replace('#','')).filter(Boolean);
  const targets = ids.map(id => document.getElementById(id)).filter(Boolean);

  function scrollToId(id, replaceHash=true){
    const el = document.getElementById(id); if(!el) return;
    const y = window.scrollY + el.getBoundingClientRect().top - (getHeaderH()+12);
    window.scrollTo({ top:y, behavior:'smooth' });
    if (replaceHash) history.replaceState(null, '', '#'+id);
  }
  tabs.forEach(a => a.addEventListener('click', e => { e.preventDefault(); scrollToId(a.getAttribute('href').slice(1)); }));

  let lastActive = null;
  function currentSection(){
    const cutoff = getHeaderH()+24;
    let cur = targets[0] || null;
    for (let i=0;i<targets.length;i++){
      const top = targets[i].getBoundingClientRect().top;
      if (top - cutoff <= 0) cur = targets[i]; else break;
    }
    return cur ? cur.id : null;
  }
  function updateActive(){
    const id = currentSection(); if(!id || id===lastActive) return;
    tabs.forEach(t => {
      const m = t.getAttribute('href').slice(1) === id;
      t.classList.toggle('is-active', m);
      t.setAttribute('aria-selected', m ? 'true' : 'false');
      if (m) t.scrollIntoView({ inline:'center', block:'nearest' });
    });
    lastActive = id;
  }
  let ticking=false;
  function onScroll(){ if(ticking) return; ticking=true; requestAnimationFrame(()=>{ updateActive(); ticking=false; }); }
  document.addEventListener('scroll', onScroll, { passive:true });
  window.addEventListener('resize', onScroll);
  window.addEventListener('load', ()=>{ updateActive(); if(location.hash) scrollToId(location.hash.slice(1), false); });

  /* ---------- 12D Carousel helpers ---------- */
  function find12dTable(root=document){
    let t = root.getElementById ? root.getElementById('twelve-d-table') : null;
    if (t) return t;
    t = root.querySelector('#codex-page table.codex-table');
    if (t) return t;
    const cands = Array.from(root.querySelectorAll('#codex-page table, .codex table'));
    for (const tbl of cands) {
      const headRow = tbl.querySelector('thead tr') || tbl.querySelector('tbody tr');
      const cols = headRow ? headRow.children.length : 0;
      const rows = tbl.querySelectorAll('tbody tr').length;
      if (cols >= 4 && rows >= 3) return tbl;
    }
    return null;
  }

  function headersFromTable(tbl){
    const thead = tbl.querySelector('thead');
    if (thead) return Array.from(thead.querySelectorAll('th')).map(th => th.textContent.trim());
    const first = tbl.querySelector('tbody tr');
    return first ? Array.from(first.children).map(td => td.textContent.trim()) : [];
  }

  function levelToClass(levelText) {
    const t = (levelText || '').toLowerCase();
    if (/(primary|major|gold)/.test(t)) return 'level-primary';
    if (/(secondary|moderate|silver)/.test(t)) return 'level-secondary';
    if (/(tertiary|minor|mini|bronze)/.test(t)) return 'level-tertiary';
    return '';
  }

  /* ---------- Build compact cards from table (keeps Level) ---------- */
  function buildCarouselFromTable(table){
    // reuse wrapper if already built
    let wrapper = table.nextElementSibling && (table.nextElementSibling.id === 'twelve-d-carousel' || table.nextElementSibling.classList.contains('codex-carousel'))
      ? table.nextElementSibling : null;

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'codex-carousel';
      wrapper.id = 'twelve-d-carousel';
      const track = document.createElement('div');
      track.className = 'cc-track';
      wrapper.appendChild(track);
      table.insertAdjacentElement('afterend', wrapper);
    }

    const track = wrapper.querySelector('.cc-track') || wrapper;
    if (!track.querySelector('.cc-card')) {
      const heads = headersFromTable(table);
      const rows  = Array.from(table.querySelectorAll('tbody tr'));
      const startIndex = table.querySelector('thead') ? 0 : 1;

      const levelIdx = heads.findIndex(h => /resonance\s*level|^level$/i.test(h));

      rows.slice(startIndex).forEach((tr, idx) => {
        const cells = Array.from(tr.children);
        if (!cells.length) return;

        const dimClass = Array.from(tr.classList).find(c => /^dim-\d+d$/.test(c)) || '';
        const levelText = levelIdx >= 0 && cells[levelIdx] ? cells[levelIdx].textContent.trim() : '';
        const levelClass = levelToClass(levelText);

        const card = document.createElement('article');
        card.className = `cc-card codex-card cc-compact ${dimClass} ${levelClass}`.trim();
        card.setAttribute('role', 'listitem');

        // Title
        const h4 = document.createElement('h4');
        h4.textContent = (cells[0].textContent || `Dimension ${idx+1}`).trim();
        card.appendChild(h4);

        // Build fields — push the Level (if present) first so users see it immediately
        const order = [];
        if (levelIdx >= 0 && cells[levelIdx]) order.push(levelIdx);
        for (let i=1;i<cells.length;i++) { if (i !== levelIdx) order.push(i); }

        order.forEach((i) => {
          const field = document.createElement('div'); field.className = 'cc-field';
          const label = document.createElement('span'); label.className = 'cc-label';
          label.textContent = (heads[i] || '').trim();
          const val = document.createElement('div'); val.innerHTML = cells[i].innerHTML;
          field.appendChild(label); field.appendChild(val);
          card.appendChild(field);
        });

        track.appendChild(card);
      });
    }
    return wrapper;
  }

  function enableSmartSwipe(el){
    let down=false, lock=null, sx=0, sy=0, sl=0;
    el.addEventListener('pointerdown', e => { down=true; lock=null; sx=e.clientX; sy=e.clientY; sl=el.scrollLeft; el.setPointerCapture?.(e.pointerId); });
    el.addEventListener('pointermove', e => {
      if(!down) return;
      const dx=e.clientX-sx, dy=e.clientY-sy;
      if(!lock && (Math.abs(dx)>6 || Math.abs(dy)>6)) lock = Math.abs(dx)>Math.abs(dy) ? 'x':'y';
      if(lock==='x'){ el.scrollLeft = sl - dx; el.classList.add('dragging'); e.preventDefault(); }
    }, { passive:false });
    function end(e){ down=false; lock=null; el.classList.remove('dragging'); el.releasePointerCapture?.(e.pointerId); }
    el.addEventListener('pointerup', end); el.addEventListener('pointercancel', end); el.addEventListener('pointerleave', end);
  }

  function setVis(wrapper, table){
    const isMobile = window.innerWidth <= 900;
    if (!wrapper) { if (table) table.style.display = ''; document.documentElement.classList.remove('codex-carousel-ready'); return; }
    if (isMobile) { wrapper.style.display='block'; if (table) table.style.display='none'; }
    else { wrapper.style.display='none'; if (table) table.style.display=''; }
  }

  function makeSureCarouselExists(){
    const table = find12dTable(document);
    if (!table) return { wrapper:null, table:null };

    const wrapper = buildCarouselFromTable(table);
    const track = wrapper.querySelector('.cc-track') || wrapper;
    if (!track.dataset.swipeReady) { enableSmartSwipe(track); track.dataset.swipeReady = '1'; }

    document.documentElement.classList.add('codex-carousel-ready');

    const dots  = wrapper.querySelectorAll('.cc-dot');
    const next  = wrapper.querySelector('.cc-next');
    const prev  = wrapper.querySelector('.cc-prev');
    function updateDots(){
      if(!dots.length) return;
      const idx = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d,i)=>d.classList.toggle('is-active', i===idx));
    }
    next && next.addEventListener('click', ()=>track.scrollBy({ left: track.clientWidth,  behavior:'smooth' }));
    prev && prev.addEventListener('click', ()=>track.scrollBy({ left:-track.clientWidth, behavior:'smooth' }));
    track.addEventListener('scroll', updateDots, { passive:true });

    return { wrapper, table };
  }

  // Build immediately if possible
  let state = makeSureCarouselExists();
  setVis(state.wrapper, state.table);

  // Late-render support
  if (!state.wrapper || !state.table) {
    const obs = new MutationObserver(() => {
      const s = makeSureCarouselExists();
      if (s.wrapper && s.table) {
        setVis(s.wrapper, s.table);
        window.addEventListener('resize', () => setVis(s.wrapper, s.table));
        window.addEventListener('orientationchange', () => setVis(s.wrapper, s.table));
        obs.disconnect();
      }
    });
    obs.observe(document.getElementById('codex-page') || document.body, { childList:true, subtree:true });
  } else {
    window.addEventListener('resize', () => setVis(state.wrapper, state.table));
    window.addEventListener('orientationchange', () => setVis(state.wrapper, state.table));
  }
})();
